/**
 * OnayMetniDetayPage.jsx — Sistem Ayarları › Onay Metinleri › bir metin (backlog #536, 2026-10-21)
 * Route: /Management/SistemAyarlari/OnayMetinleri/:key   İzin: page.sistem (okumalar personel; yazmalar onay_metni.manage)
 *
 * Bir onay metninin sürümleri ve her sürümün TR / EN gövdesi. YAYINLANMIŞ sürüm donar: değişiklik yeni bir TASLAKtır
 * (güncel sürümden, iki dil dolu açılır), taslak düzenlenir, istenirse silinir ve YAYINLANIR; yayınlamak herkesin metni
 * yeniden onaylamasını ister ve geri alınamaz (onay penceresi bunu açıkça söyler). Gövde HTML'dir: solda kaynak, sağda
 * kullanıcının göreceği arındırılmış önizleme (GuvenliHtml). Bir sürüm bir dili eksik bırakabilir; sunucu o dil için
 * Türkçeyi gösterir. Türkçe gövde yoksa sürüm yayınlanmaz: İngilizcesi tek başına kullanıcıya boş kutu gösterir.
 * Sözleşme: docs/guides/FE-OnayMetniController-guide.md.
 *
 * #553 (backend #552): bir dilin gövdesi HTML ve/veya DOSYALAR (PDF, jpg, png) olabilir: HTML üstte, dosyalar sırayla altta. Dosyalar
 * yalnızca TASLAKTA yüklenir, silinir ve sıralanır; her işlem sunucuya anında yazılır (Kaydet'e bağlı değil, "kaydedilmedi" saymaz),
 * bu yüzden ilk taslak önce kaydedilmelidir. Önizleme okuyucuların gördüğüyle aynı bileşendir (OnayMetniGovdesi). Taahhütname metninin
 * gövdesi HTML kalır. Bir sürüm global (fuarId yok) ya da tek bir FUARA özel olabilir; fuar sürümlerinin kendi satırı ve "güncel"i vardır.
 *
 * #625 (backend #624): "Etkin" HAT başına tutulur. Tanım kartındaki kutu yalnızca GENEL sürümün hattıdır (kullanıcı metninde "Genel sürüm
 * etkin"); her fuarın hattı "Yürürlük" kartında kendi düğmesiyle açılıp kapanır ve ANINDA sunucuya yazılır (Kaydet'e bağlı değil).
 * Kapalı fuar hattında metin o fuarda hiç sorulmaz (ne fuarın kendi sürümü ne genel sürüm); kendi sürümü olmayan bir fuar da
 * kapatılabilir. Kişi / taahhütname metinlerinin yalnızca genel hattı vardır, "Etkin" olduğu gibi kalır.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Table, Button, Alert, Spinner, Modal, ModalHeader, ModalBody, ModalFooter,
  Label, Input, Row, Col, Nav, NavItem, NavLink,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import OnayMetniGovdesi from "../../../Components/Common/OnayMetniGovdesi";
import { fuarService } from "../../../services/fuarService";
import i18n, { currentLocaleTag } from "../../../i18n";
import { useYetki, PERMISSION_KEYS } from "../../../services/rbacService";
import {
  listOnayMetinleri, listSurumler, getSurumMetinleri, createSurum, updateSurum, publishSurum, deleteSurum,
  updateOnayMetni, metinlerGovdesi, uploadDosya, deleteDosya, siralaDosyalar, DOSYA_UZANTILARI, DOSYA_TURU, DIL, DILLER, HEDEF,
  getFuarHatlari, setFuarHatti,
} from "../../../services/onayMetniService";
import { DilIsaretleri, DIL_KODU, metinBasligi, hedefAdi, onaySayisi, tarihYaz } from "./OnayMetniParcalari";

const LISTE_YOLU = "/Management/SistemAyarlari/OnayMetinleri";

const bosMetinler = () => ({ [DIL.TR]: { baslik: "", icerik: "", dosyalar: [] }, [DIL.EN]: { baslik: "", icerik: "", dosyalar: [] } });
/** Sunucunun { 1: {...} } cevabını her iki dili de içeren düzenleyici durumuna getirir. */
function tamMetinler(m) {
  const out = bosMetinler();
  for (const d of DILLER) if (m?.[d]) out[d] = { baslik: m[d].baslik || "", icerik: m[d].icerik || "", dosyalar: m[d].dosyalar || [] };
  return out;
}
/** Dilin gövdesi var mı: HTML'i ya da en az bir dosyası (ikisi birden boş olamaz). */
const doluMu = (m, d) => String(m?.[d]?.icerik ?? "").trim() !== "" || (m?.[d]?.dosyalar?.length ?? 0) > 0;
/** Kaydet'e bağlı kısım (başlık + HTML). Dosyalar yüklenince sunucuya anında yazılır ve burada sayılmaz. */
const yazilanKisim = (m) => JSON.stringify(DILLER.map((d) => [m[d].baslik, m[d].icerik]));
const boyutYaz = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const uzantiGecerli = (ad) => DOSYA_UZANTILARI.some((u) => String(ad).toLowerCase().endsWith(u));

export default function OnayMetniDetayPage() {
  const { t } = useTranslation("ayarlar");
  const { key } = useParams();
  const yazabilir = useYetki(PERMISSION_KEYS.ONAY_METNI_MANAGE) === true;
  const dil = i18n.language === "en" ? "en" : "tr";

  const [metin, setMetin] = useState(undefined);          // undefined yükleniyor, null bulunamadı
  const [surumler, setSurumler] = useState(null);
  const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
  const [yenile, setYenile] = useState(0);
  const [mesaj, setMesaj] = useState(null);

  // Düzenleyici: { surumId (null = henüz kaydedilmemiş ilk taslak), surumNo, taslak, metinler, ilk } ya da null.
  const [duzenle, setDuzenle] = useState(null);
  const [aktifDil, setAktifDil] = useState(DIL.TR);
  const [aciliyor, setAciliyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [duzenleHatasi, setDuzenleHatasi] = useState(null);

  const dosyaGirdisi = useRef(null);
  const [dosyaIsleniyor, setDosyaIsleniyor] = useState(false);
  const [dosyaHatasi, setDosyaHatasi] = useState(null);
  const [fuarModal, setFuarModal] = useState(null);       // { fuarId, globaldenBasla, hata } ya da null
  const [fuarlar, setFuarlar] = useState([]);
  const [fuarHatlari, setFuarHatlari] = useState(null);   // null = okunamadı / henüz yok; [] = kayıt yok
  const [fuarHatHatasi, setFuarHatHatasi] = useState(null);
  const [hatIsleniyor, setHatIsleniyor] = useState(null); // düğmesi yazılan fuarın id'si
  const [kapatilacakFuar, setKapatilacakFuar] = useState("");

  const [ayar, setAyar] = useState(null);                 // { isRequired, isActive, sortOrder } düzenlenen tanım
  const [ayarKaydediliyor, setAyarKaydediliyor] = useState(false);

  const [yayinlanacak, setYayinlanacak] = useState(null); // sürüm satırı
  const [silinecek, setSilinecek] = useState(null);       // sürüm satırı
  const [isleniyor, setIsleniyor] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState(null);

  useEffect(() => { document.title = i18n.t("ayarlar:onayMetinleri.detaySayfaBasligi", { key }); }, [key]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        let hatHatasi = null;
        const [liste, sur, hatlar] = await Promise.all([
          listOnayMetinleri(), listSurumler(key),
          // Fuar hatları okunamazsa sayfa açılır, yalnızca "Yürürlük" kartı hata gösterir.
          getFuarHatlari(key).catch((e) => { hatHatasi = e.message; return null; }),
        ]);
        if (ignore) return;
        const satir = liste.find((m) => m.onayMetniKey === key) || null;
        setMetin(satir);
        setSurumler(sur);
        setFuarHatlari(hatlar);
        setFuarHatHatasi(hatHatasi);
        setAyar(satir ? { isRequired: satir.isRequired, isActive: satir.isActive, sortOrder: satir.sortOrder } : null);
        setYuklemeHatasi(null);
      } catch (e) {
        if (ignore) return;
        setYuklemeHatasi(e.message);
        setMetin((onceki) => (onceki === undefined ? null : onceki));
        setSurumler((onceki) => onceki || []);
      }
    }
    run();
    return () => { ignore = true; };
  }, [key, yenile]);

  // Fuar adları yalnızca "fuara özel taslak" seçicisi için; okunamazsa seçici boş kalır.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await fuarService.getFuarList();
        if (!ignore) setFuarlar(list);
      } catch {
        if (!ignore) setFuarlar([]);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  const kirli = useMemo(() => !!duzenle && yazilanKisim(duzenle.metinler) !== yazilanKisim(duzenle.ilk), [duzenle]);
  // Her hattın (global ya da bir fuar) en yeni yayınlanmış sürümü "güncel"dir; "yeni taslak" ve dosya kaynağı GLOBAL hattı kullanır.
  const guncelSatirlari = useMemo(() => {
    const m = new Map();
    for (const s of surumler || []) if (!s.taslak && !m.has(s.fuarId ?? 0)) m.set(s.fuarId ?? 0, s);
    return m;
  }, [surumler]);
  const guncel = guncelSatirlari.get(0) || null;
  const acikTaslak = useMemo(() => (surumler || []).find((s) => s.taslak && s.fuarId == null) || null, [surumler]);
  // Fuara özel taslak genel sürümden başlayabilir: yayınlanmış güncel yoksa açık genel taslaktan (yayınlamadan şablon olarak kullanılır).
  const kaynak = guncel || acikTaslak;
  const ayarKirli = !!metin && !!ayar && (
    ayar.isRequired !== metin.isRequired || ayar.isActive !== metin.isActive || Number(ayar.sortOrder) !== Number(metin.sortOrder));

  // Daha önce dolu olan bir dili boşaltmak taslaktan çıkarmaz: sunucu gönderilmeyen dile dokunmaz.
  const bosaltilanDil = duzenle && duzenle.taslak && duzenle.surumId != null
    ? DILLER.find((d) => doluMu(duzenle.ilk, d) && !doluMu(duzenle.metinler, d)) || null
    : null;

  const kaydedilmemisSor = useCallback(
    () => !kirli || window.confirm(t("onayMetinleri.kaydedilmemisDegisiklik")), [kirli, t]);

  async function surumAc(s) {
    if (!kaydedilmemisSor()) return;
    setAciliyor(true);
    setDuzenleHatasi(null);
    try {
      const metinler = tamMetinler(await getSurumMetinleri(s.surumId));
      setDuzenle({ surumId: s.surumId, surumNo: s.surumNo, taslak: s.taslak, metinler, ilk: metinler });
      setAktifDil(metinler[DIL.TR].icerik || !metinler[DIL.EN].icerik ? DIL.TR : DIL.EN);
    } catch (e) {
      setMesaj({ color: "danger", text: e.message });
    } finally {
      setAciliyor(false);
    }
  }

  /** Güncel yayınlanmış sürümden yeni taslak: iki dil dolu, hemen kaydedilir ve düzenleyicide açılır. */
  async function yeniTaslak() {
    if (!kaydedilmemisSor()) return;
    setMesaj(null);
    setDuzenleHatasi(null);
    if (!guncel) {
      // Hiç sürüm yok: boş düzenleyici, ilk kayıtta taslak oluşur (en az bir dilin gövdesi gerekir).
      const bos = bosMetinler();
      setDuzenle({ surumId: null, surumNo: null, taslak: true, metinler: bos, ilk: bos });
      setAktifDil(DIL.TR);
      return;
    }
    setAciliyor(true);
    try {
      const metinler = tamMetinler(await getSurumMetinleri(guncel.surumId));
      // Dosyalar da kopyalanır (aynı baytlar, yeni dosya kimlikleri): düzenleyici yeni taslağın kendi satırlarını okur.
      const surumId = await createSurum(key, metinler, { dosyaKaynakSurumId: guncel.surumId });
      const yeni = tamMetinler(await getSurumMetinleri(surumId));
      setYenile((n) => n + 1);
      setDuzenle({ surumId, surumNo: (guncel.surumNo || 0) + 1, taslak: true, metinler: yeni, ilk: yeni });
      setAktifDil(DIL.TR);
      setMesaj({ color: "success", text: t("onayMetinleri.taslakOlusturuldu", { no: (guncel.surumNo || 0) + 1, kaynak: guncel.surumNo }) });
    } catch (e) {
      setMesaj({ color: "danger", text: e.message });
      setYenile((n) => n + 1);   // yarım yazma olabilir: sunucudakini yeniden oku
    } finally {
      setAciliyor(false);
    }
  }

  async function kaydet() {
    setKaydediliyor(true);
    setDuzenleHatasi(null);
    try {
      let surumId = duzenle.surumId;
      // Yeni taslağın numarası sunucuda en büyük numara + 1 (silinmiş taslağın numarası yeniden kullanılır).
      let surumNo = duzenle.surumNo;
      if (surumId == null) {
        surumId = await createSurum(key, duzenle.metinler, { fuarId: duzenle.fuarId });
        surumNo = (surumler || []).reduce((m, s) => Math.max(m, s.surumNo || 0), 0) + 1;
      } else await updateSurum(surumId, duzenle.metinler);
      setDuzenle((d) => ({ ...d, surumId, surumNo, ilk: d.metinler }));
      setMesaj({ color: "success", text: t("onayMetinleri.taslakKaydedildi") });
      setYenile((n) => n + 1);
    } catch (e) {
      setDuzenleHatasi(e.message);
      setYenile((n) => n + 1);   // reddedilen istek yarım taslak bırakmış olabilir
    } finally {
      setKaydediliyor(false);
    }
  }

  async function ayarKaydet() {
    setAyarKaydediliyor(true);
    setMesaj(null);
    try {
      await updateOnayMetni(key, { isRequired: !!ayar.isRequired, isActive: !!ayar.isActive, sortOrder: Number(ayar.sortOrder) || 0 });
      setMesaj({ color: "success", text: t("onayMetinleri.ayarlarKaydedildi") });
      setYenile((n) => n + 1);
    } catch (e) {
      setMesaj({ color: "danger", text: e.message });
    } finally {
      setAyarKaydediliyor(false);
    }
  }

  async function yayinla() {
    setIsleniyor(true);
    setIslemHatasi(null);
    try {
      await publishSurum(yayinlanacak.surumId);
      setMesaj({ color: "success", text: t("onayMetinleri.yayinlandi", { no: yayinlanacak.surumNo }) });
      if (duzenle && duzenle.surumId === yayinlanacak.surumId) setDuzenle(null);
      setYayinlanacak(null);
      setYenile((n) => n + 1);
    } catch (e) {
      setIslemHatasi(e.message);
    } finally {
      setIsleniyor(false);
    }
  }

  async function taslakSil() {
    setIsleniyor(true);
    setIslemHatasi(null);
    try {
      await deleteSurum(silinecek.surumId);
      setMesaj({ color: "success", text: t("onayMetinleri.taslakSilindi", { no: silinecek.surumNo }) });
      if (duzenle && duzenle.surumId === silinecek.surumId) setDuzenle(null);
      setSilinecek(null);
      setYenile((n) => n + 1);
    } catch (e) {
      setIslemHatasi(e.message);
    } finally {
      setIsleniyor(false);
    }
  }

  /** Bir dilin dosya listesini düzenleyicide günceller; sunucuya zaten yazılmıştır, bu yüzden "ilk"e de işlenir (kirli sayılmaz). */
  function dosyalariYaz(d, liste) {
    setDuzenle((x) => ({
      ...x,
      metinler: { ...x.metinler, [d]: { ...x.metinler[d], dosyalar: liste } },
      ilk: { ...x.ilk, [d]: { ...x.ilk[d], dosyalar: liste } },
    }));
  }

  async function dosyalarSecildi(e) {
    const secilen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!secilen.length || !duzenle?.surumId) return;
    const d = aktifDil;
    setDosyaHatasi(null);
    setDosyaIsleniyor(true);
    let liste = duzenle.metinler[d].dosyalar;
    try {
      for (const dosya of secilen) {
        if (!uzantiGecerli(dosya.name)) throw new Error(t("onayMetinleri.dosyaUzantiHatasi", { ad: dosya.name }));
        const yeni = await uploadDosya(duzenle.surumId, d, dosya);
        if (yeni) { liste = [...liste, yeni]; dosyalariYaz(d, liste); }
      }
      setYenile((n) => n + 1);   // sürüm listesindeki dosya sayısı ve dil işaretleri
    } catch (err) {
      setDosyaHatasi(err.message);
    } finally {
      setDosyaIsleniyor(false);
    }
  }

  async function dosyaSil(dosya) {
    setDosyaHatasi(null);
    setDosyaIsleniyor(true);
    try {
      await deleteDosya(duzenle.surumId, dosya.dosyaId);
      dosyalariYaz(aktifDil, duzenle.metinler[aktifDil].dosyalar.filter((x) => x.dosyaId !== dosya.dosyaId));
      setYenile((n) => n + 1);
    } catch (err) {
      setDosyaHatasi(err.message);
    } finally {
      setDosyaIsleniyor(false);
    }
  }

  async function dosyaTasi(indeks, yon) {
    const liste = [...duzenle.metinler[aktifDil].dosyalar];
    const hedefIndeks = indeks + yon;
    if (hedefIndeks < 0 || hedefIndeks >= liste.length) return;
    [liste[indeks], liste[hedefIndeks]] = [liste[hedefIndeks], liste[indeks]];
    setDosyaHatasi(null);
    setDosyaIsleniyor(true);
    try {
      dosyalariYaz(aktifDil, await siralaDosyalar(duzenle.surumId, aktifDil, liste.map((x) => x.dosyaId)));
    } catch (err) {
      setDosyaHatasi(err.message);
    } finally {
      setDosyaIsleniyor(false);
    }
  }

  /** "Fuara özel taslak": genel sürümden (metin + dosyalar) ya da boş başlar; sürüm bir fuara bağlanır ve sonradan değişmez. */
  async function fuarTaslagiAc() {
    const fuarId = Number(fuarModal.fuarId);
    if (!fuarId) { setFuarModal((m) => ({ ...m, hata: t("onayMetinleri.fuarSecin") })); return; }
    setIsleniyor(true);
    setFuarModal((m) => ({ ...m, hata: null }));
    try {
      if (fuarModal.globaldenBasla && kaynak) {
        const metinler = tamMetinler(await getSurumMetinleri(kaynak.surumId));
        const surumId = await createSurum(key, metinler, { fuarId, dosyaKaynakSurumId: kaynak.surumId });
        const yeni = tamMetinler(await getSurumMetinleri(surumId));
        const surumNo = (surumler || []).reduce((m, x) => Math.max(m, x.surumNo || 0), 0) + 1;
        setDuzenle({ surumId, surumNo, taslak: true, fuarId, metinler: yeni, ilk: yeni });
      } else {
        const bos = bosMetinler();
        setDuzenle({ surumId: null, surumNo: null, taslak: true, fuarId, metinler: bos, ilk: bos });
      }
      setAktifDil(DIL.TR);
      setFuarModal(null);
      setYenile((n) => n + 1);
    } catch (e) {
      setFuarModal((m) => ({ ...m, hata: e.message }));
      setYenile((n) => n + 1);   // yarım yazma olabilir
    } finally {
      setIsleniyor(false);
    }
  }

  const fuarAdi = (x) => (dil === "en" ? x.fuarAdiEn || x.fuarAdiTr : x.fuarAdiTr || x.fuarAdiEn) || `#${x.fuarId}`;

  /** Bir fuar hattını açar / kapatır; sunucuya anında yazılır ve hatlar yeniden okunur. */
  async function hatDegistir(fuarId, adi, aktif) {
    setHatIsleniyor(fuarId);
    setMesaj(null);
    try {
      await setFuarHatti(key, fuarId, aktif);
      setMesaj({ color: "success", text: t(aktif ? "onayMetinleri.fuarHattiAcildi" : "onayMetinleri.fuarHattiKapandi", { fuar: adi }) });
      setKapatilacakFuar("");
      setYenile((n) => n + 1);
    } catch (e) {
      setMesaj({ color: "danger", text: e.message });
    } finally {
      setHatIsleniyor(null);
    }
  }

  /** Hattın şu an yürürlükte olan sürümü: kapalıysa yok; açıkken fuarın kendi yayınlanmış sürümü, yoksa genel sürüm (genel hat açıksa). */
  function yururluktekiSurum(h) {
    if (!h.isActive) return null;
    if (h.guncelSurumNo != null) return { fuara: true, no: h.guncelSurumNo };
    if (metin?.isActive && guncel) return { fuara: false, no: guncel.surumNo };
    return null;
  }

  function metniGuncelle(d, alan, deger) {
    setDuzenle((x) => ({ ...x, metinler: { ...x.metinler, [d]: { ...x.metinler[d], [alan]: deger } } }));
  }

  const yazmaIpucu = yazabilir ? undefined : t("onayMetinleri.yazmaYetkisi");
  const salt = !yazabilir || !duzenle?.taslak;
  const hedef = metin?.hedef ?? HEDEF.KULLANICI;
  // Dosya yazma: yetkili, taslakta, sunucuda kayıtlı bir sürümde (dosya ona bağlanır) ve taahhütname dışında.
  const dosyaYazabilir = yazabilir && !!duzenle?.taslak && duzenle?.surumId != null;
  const dosyaIpucu = !yazabilir ? yazmaIpucu : (duzenle?.surumId == null ? t("onayMetinleri.dosyaOnceKaydedin") : undefined);
  const duzenleSatir = (surumler || []).find((x) => x.surumId === duzenle?.surumId) || null;
  const duzenleFuarId = duzenle?.fuarId ?? duzenleSatir?.fuarId ?? null;
  const duzenleFuarAdi = duzenleFuarId == null ? null
    : (duzenleSatir?.fuarId != null ? fuarAdi(duzenleSatir) : (fuarlar.find((x) => x.fuarId === duzenleFuarId)?.fuarName || `#${duzenleFuarId}`));
  const onay = metin ? onaySayisi(metin) : null;
  // Yayınlanacak sürümün HATTI kapalı mı (sunucunun hatEtkin'i: genel hat ya da o fuarın hattı); eski sunucuda Tanım kutusuna bakılır.
  const hatKapali = !!yayinlanacak && (yayinlanacak.hatEtkin != null ? yayinlanacak.hatEtkin === false : !!ayar && !ayar.isActive);

  if (metin === undefined) {
    return (
      <div className="page-content"><Container fluid><div className="text-center py-5"><Spinner color="primary" /></div></Container></div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Başlıkta anahtar değil metnin adı: tema başlığı büyük harfe çevirir, "giris" anahtarı "GİRİS" diye okunurdu. */}
        <BreadCrumb title={(metin && metinBasligi(metin, dil)) || key} pageTitle={t("onayMetinleri.baslik")} />
        <div className="mb-3">
          <Link to={LISTE_YOLU} className="small"><i className="bx bx-arrow-back me-1" />{t("onayMetinleri.listeyeDon")}</Link>
        </div>

        {mesaj && <Alert color={mesaj.color} className="py-2" toggle={() => setMesaj(null)}>{mesaj.text}</Alert>}
        {yuklemeHatasi && <Alert color="danger">{yuklemeHatasi}</Alert>}

        {metin === null ? (
          !yuklemeHatasi && <Alert color="warning">{t("onayMetinleri.metinBulunamadi", { key })}</Alert>
        ) : (
          <>
            {/* Tanım */}
            <Card className="mb-3">
              <CardBody>
                <Row className="g-3 align-items-center">
                  <Col md={6}>
                    <div className="onay-anahtar fw-semibold">{metin.onayMetniKey}</div>
                    <div className="fs-5">{metinBasligi(metin, dil) || "—"}</div>
                    <div className="small text-muted">
                      {hedefAdi(t, metin.hedef)}
                      {onay != null && ` · ${t("onayMetinleri.guncelSurumuOnaylayan", { n: onay })}`}
                    </div>
                  </Col>
                  <Col md={6}>
                    <Row className="g-2 align-items-end justify-content-md-end">
                      <Col xs="auto">
                        <div className="form-check mb-1">
                          <Input id="om-d-zorunlu" type="checkbox" className="form-check-input" checked={!!ayar?.isRequired} disabled={!yazabilir}
                            onChange={(e) => setAyar((a) => ({ ...a, isRequired: e.target.checked }))} />
                          <Label for="om-d-zorunlu" className="form-check-label small">{t("onayMetinleri.zorunlu")}</Label>
                        </div>
                        <div className="form-check">
                          <Input id="om-d-etkin" type="checkbox" className="form-check-input" checked={!!ayar?.isActive} disabled={!yazabilir}
                            onChange={(e) => setAyar((a) => ({ ...a, isActive: e.target.checked }))} />
                          <Label for="om-d-etkin" className="form-check-label small">
                            {hedef === HEDEF.KULLANICI ? t("onayMetinleri.genelSurumEtkin") : t("onayMetinleri.etkin")}
                          </Label>
                        </div>
                      </Col>
                      <Col xs={3} lg={2}>
                        <Label for="om-d-sira" className="small mb-1">{t("onayMetinleri.sira")}</Label>
                        <Input id="om-d-sira" type="number" bsSize="sm" value={ayar?.sortOrder ?? ""} disabled={!yazabilir}
                          onChange={(e) => setAyar((a) => ({ ...a, sortOrder: e.target.value }))} />
                      </Col>
                      <Col xs="auto">
                        <span title={yazmaIpucu}>
                          <Button color="primary" size="sm" disabled={!yazabilir || !ayarKirli || ayarKaydediliyor} onClick={ayarKaydet}>
                            {ayarKaydediliyor && <Spinner size="sm" className="me-1" />}{t("common:kaydet")}
                          </Button>
                        </span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                {ayar && !ayar.isActive && (
                  <div className="small text-muted mt-2">
                    {hedef === HEDEF.KULLANICI ? t("onayMetinleri.genelSurumPasifNot") : t("onayMetinleri.pasifNot")}
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Sürümler */}
            <Card className="mb-3">
              <CardHeader className="d-flex align-items-center justify-content-between py-2">
                <h6 className="mb-0">{t("onayMetinleri.surumler")}</h6>
                <div className="d-flex align-items-center gap-2">
                  {(hedef === HEDEF.KULLANICI || hedef === HEDEF.TAAHHUTNAME || hedef === HEDEF.TALEP) && (
                    <span title={yazmaIpucu}>
                      <Button color="light" size="sm" disabled={!yazabilir || aciliyor} data-testid="fuar-taslagi"
                        onClick={() => setFuarModal({ fuarId: "", globaldenBasla: !!kaynak, hata: null })}>
                        <i className="bx bx-calendar-event me-1" />{t("onayMetinleri.yeniFuarTaslagi")}
                      </Button>
                    </span>
                  )}
                  <span title={yazmaIpucu || (acikTaslak ? t("onayMetinleri.zatenTaslakVar") : undefined)}>
                    <Button color="primary" size="sm" disabled={!yazabilir || !!acikTaslak || aciliyor} onClick={yeniTaslak} data-testid="yeni-taslak">
                      {aciliyor ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-plus me-1" />}
                      {guncel ? t("onayMetinleri.guncelSurumdenTaslak") : t("onayMetinleri.ilkTaslak")}
                    </Button>
                  </span>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                {hedef === HEDEF.TAAHHUTNAME && (
                  <div className="small text-muted px-3 py-2 border-bottom" data-testid="taahhutname-notu">{t("onayMetinleri.taahhutnameNotu")}</div>
                )}
                {hedef === HEDEF.TALEP && (
                  <div className="small text-muted px-3 py-2 border-bottom" data-testid="talep-notu">{t("onayMetinleri.talepNotu")}</div>
                )}
                {surumler == null ? (
                  <div className="text-center py-4"><Spinner color="primary" /></div>
                ) : surumler.length === 0 ? (
                  <div className="text-muted p-3">{t("onayMetinleri.henuzSurumYok")}</div>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle mb-0" size="sm">
                      <thead className="text-muted small">
                        <tr>
                          <th className="ps-3">{t("onayMetinleri.surum")}</th><th>{t("onayMetinleri.durum")}</th><th>{t("onayMetinleri.diller")}</th>
                          <th>{t("onayMetinleri.yayinTarihi")}</th><th>{t("onayMetinleri.yayinlayan")}</th><th className="text-end">{t("onayMetinleri.onay")}</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        {surumler.map((s) => {
                          const buSurumKirli = kirli && duzenle.surumId === s.surumId;
                          return (
                            <tr key={s.surumId} className={duzenle?.surumId === s.surumId ? "table-active" : ""} data-testid={`surum-${s.surumNo}`}>
                              <td className="ps-3 fw-semibold">
                                v{s.surumNo}
                                {s.fuarId != null && (
                                  <span className="onay-isaret onay-isaret--eski ms-2 fw-normal" title={t("onayMetinleri.fuaraOzel")}>{fuarAdi(s)}</span>
                                )}
                              </td>
                              <td>
                                {s.taslak
                                  ? <span className="onay-isaret onay-isaret--taslak">{t("onayMetinleri.taslak")}</span>
                                  : s.surumId === guncelSatirlari.get(s.fuarId ?? 0)?.surumId
                                    ? <span className="onay-isaret onay-isaret--yayinda">{t("onayMetinleri.guncel")}</span>
                                    : <span className="onay-isaret onay-isaret--eski">{t("onayMetinleri.eskiSurum")}</span>}
                                {!s.taslak && s.surumId === guncelSatirlari.get(s.fuarId ?? 0)?.surumId && s.hatEtkin === false && (
                                  <span className="onay-isaret onay-isaret--taslak ms-1" title={t("onayMetinleri.hatKapali")}>{t("onayMetinleri.hatKapali")}</span>
                                )}
                              </td>
                              <td>
                                <DilIsaretleri diller={s.diller} />
                                {s.dosyaSayisi > 0 && (
                                  <span className="small text-muted ms-2" title={t("onayMetinleri.dosyaSayisi", { n: s.dosyaSayisi })}>
                                    <i className="bx bx-paperclip" />{s.dosyaSayisi}
                                  </span>
                                )}
                              </td>
                              <td className="text-nowrap small">{tarihYaz(s.yayinTarihi, currentLocaleTag())}</td>
                              <td>{s.yayinlayan != null ? `#${s.yayinlayan}` : "—"}</td>
                              <td className="text-end">{hedef === HEDEF.KULLANICI && !s.taslak ? s.onaySayisi : "—"}</td>
                              <td className="text-end pe-3 text-nowrap">
                                <Button color="light" size="sm" className="me-1" disabled={aciliyor} onClick={() => surumAc(s)}>
                                  {s.taslak ? t("onayMetinleri.duzenle") : t("onayMetinleri.goruntule")}
                                </Button>
                                {s.taslak && (
                                  <>
                                    <span title={yazmaIpucu || (buSurumKirli ? t("onayMetinleri.onceKaydedin") : undefined)}>
                                      <Button color="primary" size="sm" className="me-1" disabled={!yazabilir || buSurumKirli}
                                        onClick={() => { setIslemHatasi(null); setYayinlanacak(s); }}>
                                        {t("onayMetinleri.yayinla")}
                                      </Button>
                                    </span>
                                    <span title={yazmaIpucu}>
                                      <Button color="link" size="sm" className="p-1 text-danger" disabled={!yazabilir}
                                        title={t("onayMetinleri.taslakSil")} onClick={() => { setIslemHatasi(null); setSilinecek(s); }}>
                                        <i className="bx bx-trash" />
                                      </Button>
                                    </span>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Yürürlük: her fuarın kendi hattı (#625) */}
            {hedef === HEDEF.KULLANICI && (
              <Card className="mb-3" data-testid="yururluk">
                <CardHeader className="py-2"><h6 className="mb-0">{t("onayMetinleri.yururluk")}</h6></CardHeader>
                <CardBody className="p-0">
                  <div className="small text-muted px-3 py-2">{t("onayMetinleri.yururlukAciklama")}</div>
                  {fuarHatHatasi && <Alert color="danger" className="py-2 small mx-3">{fuarHatHatasi}</Alert>}
                  {fuarHatlari == null ? (
                    !fuarHatHatasi && <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>
                  ) : fuarHatlari.length === 0 ? (
                    <div className="small text-muted px-3 pb-2">{t("onayMetinleri.fuarHatlariYok")}</div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0" size="sm">
                        <thead className="text-muted small">
                          <tr>
                            <th className="ps-3">{t("onayMetinleri.fuar")}</th><th>{t("onayMetinleri.yururluktekiSurum")}</th>
                            <th>{t("onayMetinleri.taslak")}</th><th>{t("onayMetinleri.sonDegisiklik")}</th>
                            <th className="text-end pe-3">{t("onayMetinleri.buFuardaEtkin")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fuarHatlari.map((h) => {
                            const yururlukte = yururluktekiSurum(h);
                            return (
                              <tr key={h.fuarId} className={h.isActive ? "" : "text-muted"} data-testid={`hat-${h.fuarId}`}>
                                <td className="ps-3 fw-semibold">{fuarAdi(h)}</td>
                                <td>
                                  {yururlukte == null
                                    ? <span className="text-muted">{t("onayMetinleri.sorulmaz")}</span>
                                    : yururlukte.fuara
                                      ? <span className="onay-isaret onay-isaret--yayinda">{t("onayMetinleri.fuarSurumu", { no: yururlukte.no })}</span>
                                      : <span className="onay-isaret onay-isaret--eski">{t("onayMetinleri.genelSurumYururlukte", { no: yururlukte.no })}</span>}
                                </td>
                                <td>{h.taslakSayisi > 0 ? <span className="onay-isaret onay-isaret--taslak">{h.taslakSayisi}</span> : "—"}</td>
                                <td className="text-nowrap small">
                                  {h.updateDate ? `${tarihYaz(h.updateDate, currentLocaleTag())}${h.guncelleyen != null ? ` · #${h.guncelleyen}` : ""}` : "—"}
                                </td>
                                <td className="text-end pe-3">
                                  <span title={yazmaIpucu}>
                                    <div className="form-check form-switch d-inline-block mb-0">
                                      <Input type="switch" role="switch" className="form-check-input" id={`om-hat-${h.fuarId}`} checked={h.isActive}
                                        disabled={!yazabilir || hatIsleniyor != null} data-testid={`hat-anahtar-${h.fuarId}`}
                                        aria-label={`${t("onayMetinleri.buFuardaEtkin")}: ${fuarAdi(h)}`}
                                        onChange={(e) => hatDegistir(h.fuarId, fuarAdi(h), e.target.checked)} />
                                    </div>
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                  <div className="px-3 py-3 border-top">
                    <div className="small text-muted mb-2">{t("onayMetinleri.fuardaKapatNot")}</div>
                    <div className="d-flex align-items-center gap-2">
                      <Input type="select" bsSize="sm" style={{ maxWidth: 320 }} value={kapatilacakFuar} data-testid="hat-fuar-sec"
                        aria-label={t("onayMetinleri.fuar")} disabled={!yazabilir || hatIsleniyor != null}
                        onChange={(e) => setKapatilacakFuar(e.target.value)}>
                        <option value="">{t("onayMetinleri.fuarSecin")}</option>
                        {fuarlar.filter((x) => !(fuarHatlari || []).some((h) => h.fuarId === x.fuarId)).map((x) => (
                          <option key={x.fuarId} value={x.fuarId}>{x.fuarName || x.fuarShortName || `#${x.fuarId}`}</option>
                        ))}
                      </Input>
                      <span title={yazmaIpucu}>
                        <Button color="light" size="sm" data-testid="hat-fuar-kapat"
                          disabled={!yazabilir || !kapatilacakFuar || hatIsleniyor != null}
                          onClick={() => hatDegistir(Number(kapatilacakFuar), fuarlar.find((x) => x.fuarId === Number(kapatilacakFuar))?.fuarName || `#${kapatilacakFuar}`, false)}>
                          {hatIsleniyor != null && <Spinner size="sm" className="me-1" />}{t("onayMetinleri.fuardaKapat")}
                        </Button>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Düzenleyici */}
            {duzenle && (
              <Card className="mb-3">
                <CardHeader className="d-flex align-items-center justify-content-between py-2">
                  <h6 className="mb-0">
                    {duzenle.surumNo != null ? `v${duzenle.surumNo}` : t("onayMetinleri.yeniTaslak")}
                    {" · "}{duzenle.taslak ? t("onayMetinleri.taslak") : t("onayMetinleri.yayinlanmisDonmus")}
                    {duzenleFuarAdi && <span className="onay-isaret onay-isaret--eski ms-2 fw-normal">{duzenleFuarAdi}</span>}
                  </h6>
                  <div className="d-flex align-items-center gap-2">
                    {kirli && <span className="small text-muted">{t("onayMetinleri.kaydedilmedi")}</span>}
                    {duzenle.taslak && (
                      <span title={yazmaIpucu}>
                        <Button color="primary" size="sm" disabled={!yazabilir || !kirli || kaydediliyor || !!bosaltilanDil || !metinlerGovdesi(duzenle.metinler).texts.length}
                          onClick={kaydet} data-testid="taslak-kaydet">
                          {kaydediliyor && <Spinner size="sm" className="me-1" />}{t("common:kaydet")}
                        </Button>
                      </span>
                    )}
                    <Button color="light" size="sm" onClick={() => { if (kaydedilmemisSor()) setDuzenle(null); }}>{t("onayMetinleri.kapat")}</Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <Nav tabs className="mb-3">
                    {DILLER.map((d) => (
                      <NavItem key={d}>
                        <NavLink href="#" active={aktifDil === d} onClick={(e) => { e.preventDefault(); setAktifDil(d); }}>
                          {DIL_KODU[d]}{" "}
                          <span className={`onay-isaret ${doluMu(duzenle.metinler, d) ? "onay-isaret--var" : "onay-isaret--yok"}`}>
                            {doluMu(duzenle.metinler, d) ? "✓" : "—"}
                          </span>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  {duzenleHatasi && <Alert color="danger" className="py-2 small">{duzenleHatasi}</Alert>}
                  {bosaltilanDil && (
                    <Alert color="warning" className="py-2 small">{t("onayMetinleri.dilCikarilamaz", { dil: DIL_KODU[bosaltilanDil] })}</Alert>
                  )}

                  <Row className="g-3">
                    <Col lg={6}>
                      <Label for="om-baslik" className="small mb-1">{t("onayMetinleri.metinBasligi")} ({DIL_KODU[aktifDil]})</Label>
                      <Input id="om-baslik" bsSize="sm" className="mb-2" value={duzenle.metinler[aktifDil].baslik} readOnly={salt} maxLength={255}
                        onChange={(e) => metniGuncelle(aktifDil, "baslik", e.target.value)} />
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <Label for="om-icerik" className="small mb-0">{t("onayMetinleri.htmlKaynagi")} ({DIL_KODU[aktifDil]})</Label>
                        {aktifDil === DIL.EN && !salt && !doluMu(duzenle.metinler, DIL.EN) && doluMu(duzenle.metinler, DIL.TR) && (
                          <Button color="link" size="sm" className="p-0" onClick={() => {
                            metniGuncelle(DIL.EN, "icerik", duzenle.metinler[DIL.TR].icerik);
                            if (!duzenle.metinler[DIL.EN].baslik) metniGuncelle(DIL.EN, "baslik", duzenle.metinler[DIL.TR].baslik);
                          }}>
                            {t("onayMetinleri.trdenKopyala")}
                          </Button>
                        )}
                      </div>
                      <Input id="om-icerik" type="textarea" className="onay-html-kaynak" spellCheck={false} value={duzenle.metinler[aktifDil].icerik}
                        readOnly={salt} placeholder={salt ? undefined : "<p>...</p>"} onChange={(e) => metniGuncelle(aktifDil, "icerik", e.target.value)} />

                      {hedef !== HEDEF.TAAHHUTNAME && (
                        <div className="mt-3" data-testid="dosyalar">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <Label className="small mb-0">{t("onayMetinleri.dosyalar")} ({DIL_KODU[aktifDil]})</Label>
                            {duzenle.taslak && (
                              <span title={dosyaIpucu}>
                                <Button color="light" size="sm" disabled={!dosyaYazabilir || dosyaIsleniyor} data-testid="dosya-yukle"
                                  onClick={() => dosyaGirdisi.current?.click()}>
                                  {dosyaIsleniyor ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-upload me-1" />}
                                  {t("onayMetinleri.dosyaYukle")}
                                </Button>
                              </span>
                            )}
                            <input ref={dosyaGirdisi} type="file" hidden multiple accept={DOSYA_UZANTILARI.join(",")} data-testid="dosya-girdisi"
                              onChange={dosyalarSecildi} />
                          </div>
                          {dosyaHatasi && <Alert color="danger" className="py-2 small" toggle={() => setDosyaHatasi(null)}>{dosyaHatasi}</Alert>}
                          {duzenle.metinler[aktifDil].dosyalar.length === 0 ? (
                            <div className="small text-muted">{t("onayMetinleri.dosyaYok")}</div>
                          ) : (
                            <div className="d-flex flex-column gap-1">
                              {duzenle.metinler[aktifDil].dosyalar.map((dosya, i, liste) => (
                                <div key={dosya.dosyaId} className="onay-dosya-satir" data-testid={`dosya-satir-${dosya.dosyaId}`}>
                                  <i className={dosya.tur === DOSYA_TURU.PDF ? "bx bxs-file-pdf text-danger fs-5" : "bx bx-image fs-5 text-muted"} />
                                  <span className="onay-dosya-satir__ad" title={dosya.orijinalAd}>{dosya.orijinalAd}</span>
                                  <span className="text-muted text-nowrap">{boyutYaz(dosya.boyut)}</span>
                                  {dosyaYazabilir && (
                                    <>
                                      <Button color="link" size="sm" className="p-0" disabled={dosyaIsleniyor || i === 0} title={t("onayMetinleri.dosyaYukari")}
                                        onClick={() => dosyaTasi(i, -1)}><i className="bx bx-up-arrow-alt" /></Button>
                                      <Button color="link" size="sm" className="p-0" disabled={dosyaIsleniyor || i === liste.length - 1} title={t("onayMetinleri.dosyaAsagi")}
                                        onClick={() => dosyaTasi(i, 1)}><i className="bx bx-down-arrow-alt" /></Button>
                                      <Button color="link" size="sm" className="p-0 text-danger" disabled={dosyaIsleniyor} title={t("onayMetinleri.dosyaSil")}
                                        onClick={() => dosyaSil(dosya)}><i className="bx bx-trash" /></Button>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="small text-muted mt-1">{t("onayMetinleri.dosyaAciklama")}</div>
                        </div>
                      )}
                    </Col>
                    <Col lg={6}>
                      <div className="small mb-1">{t("onayMetinleri.onizleme")}</div>
                      <div className="border rounded p-3 onay-onizleme" data-testid="onizleme">
                        {duzenle.metinler[aktifDil].baslik && <h5 className="mb-3">{duzenle.metinler[aktifDil].baslik}</h5>}
                        {doluMu(duzenle.metinler, aktifDil)
                          ? <OnayMetniGovdesi icerik={duzenle.metinler[aktifDil].icerik} dosyalar={duzenle.metinler[aktifDil].dosyalar} />
                          : <span className="text-muted small">{t("onayMetinleri.dilGovdesiYok", { dil: DIL_KODU[aktifDil] })}</span>}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </>
        )}

        {/* Fuara özel taslak */}
        <Modal isOpen={!!fuarModal} toggle={() => !isleniyor && setFuarModal(null)} centered>
          {fuarModal && (
            <>
              <ModalHeader toggle={() => !isleniyor && setFuarModal(null)}>{t("onayMetinleri.fuarTaslakBaslik")}</ModalHeader>
              <ModalBody className="small">
                <p>{t(hedef === HEDEF.TAAHHUTNAME ? "onayMetinleri.fuarTaslakAciklamaTaahhutname"
                  : hedef === HEDEF.TALEP ? "onayMetinleri.fuarTaslakAciklamaTalep" : "onayMetinleri.fuarTaslakAciklama")}</p>
                <Label for="om-fuar-sec" className="small mb-1">{t("onayMetinleri.fuar")}</Label>
                <Input id="om-fuar-sec" type="select" bsSize="sm" value={fuarModal.fuarId} data-testid="fuar-sec"
                  onChange={(e) => setFuarModal((m) => ({ ...m, fuarId: e.target.value }))}>
                  <option value="">{t("onayMetinleri.fuarSecin")}</option>
                  {fuarlar.map((x) => <option key={x.fuarId} value={x.fuarId}>{x.fuarName || x.fuarShortName || `#${x.fuarId}`}</option>)}
                </Input>
                {kaynak && (
                  <div className="form-check mt-3">
                    <Input id="om-fuar-global" type="checkbox" className="form-check-input" checked={fuarModal.globaldenBasla}
                      onChange={(e) => setFuarModal((m) => ({ ...m, globaldenBasla: e.target.checked }))} />
                    <Label for="om-fuar-global" className="form-check-label small">
                      {t(kaynak.taslak ? "onayMetinleri.globalTaslaktanBasla" : "onayMetinleri.globaldenBasla", { no: kaynak.surumNo })}
                    </Label>
                  </div>
                )}
                {fuarModal.hata && <Alert color="danger" className="py-2 mt-3 mb-0">{fuarModal.hata}</Alert>}
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={() => setFuarModal(null)} disabled={isleniyor}>{t("onayMetinleri.vazgec")}</Button>
                <Button color="primary" onClick={fuarTaslagiAc} disabled={isleniyor} data-testid="fuar-taslagi-olustur">
                  {isleniyor && <Spinner size="sm" className="me-1" />}{t("onayMetinleri.olustur")}
                </Button>
              </ModalFooter>
            </>
          )}
        </Modal>

        {/* Yayınla */}
        <Modal isOpen={!!yayinlanacak} toggle={() => !isleniyor && setYayinlanacak(null)} centered>
          {yayinlanacak && (
            <>
              <ModalHeader toggle={() => !isleniyor && setYayinlanacak(null)}>{t("onayMetinleri.yayinlaBaslik", { no: yayinlanacak.surumNo })}</ModalHeader>
              <ModalBody className="small">
                {!yayinlanacak.diller.includes(DIL.TR) ? (
                  <Alert color="danger" className="py-2 mb-0">{t("onayMetinleri.trYokYayinlanamaz")}</Alert>
                ) : (
                  <>
                    <p className="fw-semibold mb-2">
                      {hedef === HEDEF.KISI ? t("onayMetinleri.yayinKisi")
                        : hedef === HEDEF.TAAHHUTNAME
                          ? (yayinlanacak.fuarId != null ? t("onayMetinleri.yayinTaahhutnameFuar", { fuar: fuarAdi(yayinlanacak) }) : t("onayMetinleri.yayinTaahhutname"))
                          : hedef === HEDEF.TALEP
                            ? (yayinlanacak.fuarId != null ? t("onayMetinleri.yayinTalepFuar", { fuar: fuarAdi(yayinlanacak) }) : t("onayMetinleri.yayinTalep"))
                            : yayinlanacak.fuarId != null ? t("onayMetinleri.yayinFuar", { fuar: fuarAdi(yayinlanacak) })
                            : t("onayMetinleri.yayinKullanici")}
                    </p>
                    {hedef === HEDEF.KULLANICI && guncel && onay > 0 && yayinlanacak.fuarId == null && (
                      <p className="mb-2">{t("onayMetinleri.yayinOnaySayisi", { n: onay, no: guncel.surumNo })}</p>
                    )}
                    {!yayinlanacak.diller.includes(DIL.EN) && (
                      <Alert color="warning" className="py-2 mb-2">{t("onayMetinleri.enYokUyari")}</Alert>
                    )}
                    {hatKapali && (
                      <Alert color="info" className="py-2 mb-2">
                        {hedef !== HEDEF.KULLANICI ? t("onayMetinleri.pasifYayinNotu")
                          : yayinlanacak.fuarId != null ? t("onayMetinleri.fuardaPasifYayinNotu") : t("onayMetinleri.genelPasifYayinNotu")}
                      </Alert>
                    )}
                    <p className="mb-0">{t("onayMetinleri.geriAlinamaz")}</p>
                  </>
                )}
                {islemHatasi && <Alert color="danger" className="py-2 mt-3 mb-0">{islemHatasi}</Alert>}
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={() => setYayinlanacak(null)} disabled={isleniyor}>{t("onayMetinleri.vazgec")}</Button>
                <Button color="danger" onClick={yayinla} disabled={isleniyor || !yayinlanacak.diller.includes(DIL.TR)} data-testid="yayinla-onay">
                  {isleniyor && <Spinner size="sm" className="me-1" />}{t("onayMetinleri.yayinla")}
                </Button>
              </ModalFooter>
            </>
          )}
        </Modal>

        {/* Taslağı at */}
        <Modal isOpen={!!silinecek} toggle={() => !isleniyor && setSilinecek(null)} centered>
          {silinecek && (
            <>
              <ModalHeader toggle={() => !isleniyor && setSilinecek(null)}>{t("onayMetinleri.taslakSilBaslik", { no: silinecek.surumNo })}</ModalHeader>
              <ModalBody className="small">
                <p className="mb-0">{t("onayMetinleri.taslakSilAciklama")}</p>
                {islemHatasi && <Alert color="danger" className="py-2 mt-3 mb-0">{islemHatasi}</Alert>}
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={() => setSilinecek(null)} disabled={isleniyor}>{t("onayMetinleri.vazgec")}</Button>
                <Button color="danger" onClick={taslakSil} disabled={isleniyor}>
                  {isleniyor && <Spinner size="sm" className="me-1" />}{t("onayMetinleri.taslakSil")}
                </Button>
              </ModalFooter>
            </>
          )}
        </Modal>
      </Container>
    </div>
  );
}
